/**
 * Service Principals Page
 *
 * Feature: 023-service-principal-registry
 * Task: T016 - Create ServicePrincipalsPage with search/filter
 * Task: T025 - Integrate form into page (Add button, modal)
 * Constitution: v1.19.0 (Multi-Subscription SQL MI Edition)
 *
 * Main page displaying all registered service principals with CRUD operations.
 *
 * Conformed to the Spec 197 design blueprint (2026-06-14): migrated off the
 * ad-hoc Tailwind header/cards/inputs/buttons + the bespoke `fixed inset-0`
 * modals onto the canonical token-driven UI kit (PageLayout / Card / DataTable /
 * Button / Input / Select / Badge / StatCard / Modal). Presentation-only — data
 * fetching, server pagination, mutations, and routing are unchanged.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, KeyRound, Plus, Pencil, Trash2, AlertTriangle, Check, X } from 'lucide-react';
import {
  ServicePrincipal,
  ServicePrincipalCategory,
  ServicePrincipalStatus,
  CreateServicePrincipalRequest,
  UpdateServicePrincipalRequest,
} from '../types';
import {
  useServicePrincipals,
  useCreateServicePrincipal,
  useUpdateServicePrincipal,
  useDeleteServicePrincipal,
  useDashboardSummary,
} from '../hooks/useServicePrincipals';
import { ServicePrincipalForm } from '../components/ServicePrincipalForm';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  Modal,
  AppTooltip,
  PageHelpPanel,
  Extended,
  QuickGlance,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { servicePrincipalHelp } from '../../../data/help/servicePrincipalHelp';
import { formatEasternDate } from '@/utils/dateFormatter';

type ModalMode = 'create' | 'edit' | null;

const PAGE_SIZE = 50;

// ============================================================================
// Helpers (presentation only)
// ============================================================================

/** Relative-time formatting for the verified-at timestamps. */
function formatDate(date: Date | string | null): string {
  if (!date) return 'Never';
  const d = new Date(date);
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60000) return 'Just now';
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)} min ago`;
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)} hours ago`;
  if (diffMs < 604800000) return `${Math.floor(diffMs / 86400000)} days ago`;
  return formatEasternDate(d);
}

const statusVariant: Record<ServicePrincipalStatus, 'success' | 'secondary' | 'danger'> = {
  ACTIVE: 'success',
  INACTIVE: 'secondary',
  DELETED: 'danger',
};

const categoryVariant: Record<ServicePrincipalCategory, 'primary' | 'info' | 'default'> = {
  Backup: 'primary',
  DataCollection: 'info',
  Monitoring: 'info',
  Integration: 'default',
};

/**
 * Service Principals Page Component
 */
export const ServicePrincipalsPage: React.FC = () => {
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(1);

  // Filters state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ServicePrincipalStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<ServicePrincipalCategory | ''>('');

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingSp, setEditingSp] = useState<ServicePrincipal | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ServicePrincipal | null>(null);

  // Queries and mutations
  const {
    data: spData,
    isLoading,
    refetch,
  } = useServicePrincipals({
    search: search || undefined,
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const { data: dashboardData } = useDashboardSummary();

  const createMutation = useCreateServicePrincipal();
  const updateMutation = useUpdateServicePrincipal();
  const deleteMutation = useDeleteServicePrincipal();

  // Get service principals from API (already filtered/paginated by server)
  const servicePrincipals = spData?.items ?? [];

  /**
   * Handle row click - navigate to detail page
   */
  const handleRowClick = useCallback(
    (sp: ServicePrincipal) => {
      navigate(`/service-principals/${sp.id}`);
    },
    [navigate]
  );

  /**
   * Handle edit click
   */
  const handleEdit = useCallback((sp: ServicePrincipal) => {
    setEditingSp(sp);
    setModalMode('edit');
  }, []);

  /**
   * Handle delete click
   */
  const handleDeleteClick = useCallback((sp: ServicePrincipal) => {
    setDeleteConfirm(sp);
  }, []);

  /**
   * Confirm delete
   */
  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteMutation.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  /**
   * Handle form submit (create or update)
   */
  const handleFormSubmit = async (
    data: CreateServicePrincipalRequest | UpdateServicePrincipalRequest
  ) => {
    try {
      if (modalMode === 'create') {
        await createMutation.mutateAsync(data as CreateServicePrincipalRequest);
      } else if (modalMode === 'edit' && editingSp) {
        await updateMutation.mutateAsync({ id: editingSp.id, data });
      }
      closeModal();
    } catch (error) {
      console.error('Form submission failed:', error);
      throw error; // Re-throw to let form handle it
    }
  };

  /**
   * Close modal and reset state
   */
  const closeModal = () => {
    setModalMode(null);
    setEditingSp(null);
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // ==========================================================================
  // Table columns (folds the former ServicePrincipalTable into DataTable)
  // ==========================================================================
  const columns: DataTableColumn<ServicePrincipal>[] = [
    {
      key: 'displayName',
      header: 'Name',
      width: '28%',
      render: (sp) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-[var(--color-text-primary)]">{sp.displayName}</span>
          <span className="truncate font-mono text-xs text-[var(--color-text-secondary)]">{sp.applicationId}</span>
          {sp.purpose && (
            <span className="mt-0.5 truncate text-xs text-[var(--color-text-secondary)]">{sp.purpose}</span>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      width: '12%',
      render: (sp) => <Badge variant={categoryVariant[sp.category]}>{sp.category}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '10%',
      render: (sp) => (
        <Badge variant={statusVariant[sp.status]} isOutlined>
          {sp.status}
        </Badge>
      ),
    },
    {
      key: 'autoFixEnabled',
      header: 'Auto-Fix',
      width: '10%',
      render: (sp) =>
        sp.autoFixEnabled ? (
          <span className="inline-flex items-center gap-1 text-[var(--color-status-success)]">
            <Check className="h-3.5 w-3.5" />
            Enabled
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[var(--color-text-secondary)]">
            <X className="h-3.5 w-3.5" />
            Disabled
          </span>
        ),
    },
    {
      key: 'lastVerified',
      header: 'Last Verified',
      width: '16%',
      render: (sp) => (
        <div className="flex flex-col text-xs text-[var(--color-text-secondary)]">
          <span>Fabric: {formatDate(sp.lastFabricVerifiedAt)}</span>
          <span>Entra: {formatDate(sp.lastEntraVerifiedAt)}</span>
        </div>
      ),
    },
    {
      key: 'assignments',
      header: 'Assignments',
      width: '10%',
      // Assignment count would come from joined data (unchanged placeholder).
      render: () => <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: '14%',
      render: (sp) => (
        <div className="flex items-center justify-end gap-1">
          <AppTooltip title="Edit">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(sp);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </AppTooltip>
          {sp.status !== 'DELETED' && (
            <AppTooltip title="Delete">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(sp);
                }}
              >
                <Trash2 className="h-4 w-4 text-[var(--color-status-danger)]" />
              </Button>
            </AppTooltip>
          )}
        </div>
      ),
    },
  ];

  const meta = spData?.meta;

  return (
    <PageLayout
      title="Service Principal Registry"
      description="Manage and monitor service principals used by the Fabric Control Plane"
      icon={<KeyRound className="h-6 w-6" />}
      actions={
        <>
          <PageHelpPanel content={servicePrincipalHelp} buttonSize="sm" />
          <AppTooltip title="Register a new service principal in the Fabric Control Plane">
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalMode('create')}>
              Register Service Principal
            </Button>
          </AppTooltip>
        </>
      }
    >
      {/* Summary tiles */}
      {dashboardData && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Total SPs" value={dashboardData.totalServicePrincipals} />
          <StatCard
            label="Active"
            value={dashboardData.activeServicePrincipals}
            valueClassName="text-[var(--color-status-success)]"
          />
          <StatCard label="Total Assignments" value={dashboardData.totalAssignments} />
          <AppTooltip title="Number of configuration drifts currently detected across service principals">
            <StatCard
              label="Active Drifts"
              value={dashboardData.activeDrifts}
              valueClassName={
                dashboardData.activeDrifts > 0 ? 'text-[var(--color-status-warning)]' : undefined
              }
              icon={dashboardData.activeDrifts > 0 ? <AlertTriangle className="h-4 w-4" /> : undefined}
            />
          </AppTooltip>
        </div>
      )}

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      <Extended>
      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
          <div className="md:col-span-6">
            <Input
              isFullWidth
              placeholder="Search by name, application ID, or purpose..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              leftAddon={<Search className="h-4 w-4" />}
            />
          </div>
          <div className="md:col-span-3">
            <Select
              isFullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as ServicePrincipalStatus | '');
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
                { value: 'DELETED', label: 'Deleted' },
              ]}
            />
          </div>
          <div className="md:col-span-2">
            <Select
              isFullWidth
              label="Category"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value as ServicePrincipalCategory | '');
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Categories' },
                { value: 'Backup', label: 'Backup' },
                { value: 'DataCollection', label: 'Data Collection' },
                { value: 'Monitoring', label: 'Monitoring' },
                { value: 'Integration', label: 'Integration' },
              ]}
            />
          </div>
          <div className="md:col-span-1 md:self-end">
            <Button
              variant="outline"
              isFullWidth
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        <DataTable
          columns={columns}
          rows={servicePrincipals}
          rowKey={(sp) => sp.id}
          pageSize={PAGE_SIZE}
          /* This page owns pagination (server-side, with its own page
             controls), so the table must not render a second one. The blueprint's
             DataTable rules name this case: "Without it you get two paginators
             stacked." */
          showPagination={false}
          onRowClick={handleRowClick}
          minWidth="52rem"
          emptyMessage={
            isLoading ? 'Loading service principals…' : 'No service principals found. Register your first one to get started.'
          }
        />

        {/* Server-side pagination (unchanged behavior; restyled to the kit) */}
        {meta && meta.total > PAGE_SIZE && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] px-4 py-3">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Showing {(meta.page - 1) * meta.pageSize + 1} to{' '}
              {Math.min(meta.page * meta.pageSize, meta.total)} of {meta.total} service principals
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setPage(1)} disabled={!meta.hasPrevPage}>
                First
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!meta.hasPrevPage}
              >
                Previous
              </Button>
              <span className="px-2 text-sm text-[var(--color-text-secondary)]">
                Page {meta.page} of {meta.totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                disabled={!meta.hasNextPage}
              >
                Next
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(meta.totalPages)}
                disabled={!meta.hasNextPage}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </Card>
      </Extended>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalMode !== null}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Register New Service Principal' : 'Edit Service Principal'}
        size="md"
      >
        <ServicePrincipalForm
          initialData={editingSp || undefined}
          onSubmit={handleFormSubmit}
          onCancel={closeModal}
          isSubmitting={isSubmitting}
          mode={modalMode ?? 'create'}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Service Principal?"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} loading={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-[var(--color-text-secondary)]">
          Are you sure you want to delete{' '}
          <strong className="text-[var(--color-text-primary)]">{deleteConfirm?.displayName}</strong>? This action
          will soft-delete the service principal and mark it as DELETED.
        </p>
        <div className="mt-4 flex items-start gap-2 rounded-md border border-[var(--color-status-warning)]/40 bg-[var(--color-status-warning)]/10 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-status-warning)]" />
          <p className="text-sm text-[var(--color-text-secondary)]">
            Any workspace assignments for this SP will remain in the database for audit purposes.
          </p>
        </div>
      </Modal>
    </PageLayout>
  );
};

export default ServicePrincipalsPage;
